/**
 * Custom hook to fetch processor prompts from the API (SWRM-103).
 *
 * Provides default prompts for a processor, which can be customized
 * per pipeline step.
 */

import { useState, useEffect } from 'react';
import { ProcessorPromptsResponse } from '../types/pipeline';

// Get API base URL (matches ApiService configuration)
const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    return `${apiUrl}/api`;
  } else if (process.env.NODE_ENV === "production") {
    return "https://api.roboworks.com/api";
  } else {
    return "http://localhost:8000/api";
  }
};

interface UseProcessorPromptsResult {
  prompts: ProcessorPromptsResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useProcessorPrompts(processorName: string | null): UseProcessorPromptsResult {
  const [prompts, setPrompts] = useState<ProcessorPromptsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    // Skip if no processor name provided
    if (!processorName) {
      setPrompts(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    // SWRM-128: Skip fetching prompts for processors that don't have them
    // INPUT processor is a utility processor without custom prompts
    const processorsWithoutPrompts = ['input'];
    if (processorsWithoutPrompts.includes(processorName.toLowerCase())) {
      setPrompts({
        prompts: {},
        phases: [],
        has_prompts: false,
        processor_name: processorName,
      });
      setError(null);
      setIsLoading(false);
      return;
    }

    const fetchPrompts = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}/processors/${processorName}/prompts`);
        
        if (!response.ok) {
          if (response.status === 404) {
            // Processor doesn't have prompts - return empty response
            setPrompts({
              prompts: {},
              phases: [],
              has_prompts: false,
              processor_name: processorName,
            });
            setError(null);
            return;
          }
          throw new Error(`Failed to fetch prompts: ${response.statusText}`);
        }

        const data: ProcessorPromptsResponse = await response.json();
        setPrompts(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        setPrompts(null);
        console.error('Error fetching processor prompts:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrompts();
  }, [processorName, refetchTrigger]);

  const refetch = () => {
    setRefetchTrigger(prev => prev + 1);
  };

  return {
    prompts,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Get default prompts as a simple dict for initializing node config.
 *
 * @param processorName - Name of the processor
 * @returns Promise<Record<string, string>> - Dict of prompt_name -> content
 */
export async function getDefaultPromptsDict(
  processorName: string
): Promise<Record<string, string>> {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/processors/${processorName}/prompts`);
    
    if (!response.ok) {
      console.warn(`Failed to fetch prompts for ${processorName}, using empty dict`);
      return {};
    }

    const data: ProcessorPromptsResponse = await response.json();
    
    if (!data.has_prompts) {
      return {};
    }

    // Convert to simple dict format
    const promptsDict: Record<string, string> = {};
    for (const [promptName, promptData] of Object.entries(data.prompts)) {
      promptsDict[promptName] = promptData.content;
    }

    return promptsDict;
  } catch (err) {
    console.error(`Error fetching default prompts for ${processorName}:`, err);
    return {};
  }
}

